import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Package, ArrowRight } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface Row {
  id: string;
  slug: string;
  title: string;
  discount_pct: number;
  thumbnail_url: string | null;
  bundle_courses: { course: { price: number | null } | null }[];
}

const fmt = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

interface Props {
  limit?: number;
  className?: string;
}

export function BundleCallout({ limit = 3, className }: Props) {
  const { data = [], isLoading } = useQuery<Row[]>({
    queryKey: ["bundle-callout", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_bundles")
        .select(
          "id, slug, title, discount_pct, thumbnail_url, bundle_courses(course:courses(price))"
        )
        .eq("is_active", true)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as unknown as Row[];
    },
    staleTime: 5 * 60_000,
  });

  if (isLoading || data.length === 0) return null;

  const usable = data.filter((b) => b.bundle_courses.length >= 2);
  if (usable.length === 0) return null;

  return (
    <section className={className}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Pacotes com desconto</h2>
        </div>
        <Button asChild variant="ghost" size="sm" className="gap-1">
          <Link to="/bundles">
            Ver todos <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {usable.map((b) => {
          const subtotal = b.bundle_courses.reduce(
            (s, bc) => s + (bc.course?.price ? Number(bc.course.price) : 0),
            0
          );
          const total = subtotal * (1 - Number(b.discount_pct) / 100);
          return (
            <Link
              key={b.id}
              to={`/bundles/${b.slug}`}
              className="group block"
            >
              <Card className="p-4 flex gap-3 hover:shadow-md transition-shadow h-full">
                <div className="flex-shrink-0 w-20 h-20 rounded-md bg-muted overflow-hidden">
                  {b.thumbnail_url ? (
                    <img
                      src={b.thumbnail_url}
                      alt={b.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                      <Package className="h-6 w-6 text-primary/40" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium line-clamp-2 group-hover:text-primary transition-colors">
                      {b.title}
                    </div>
                    <Badge variant="secondary" className="flex-shrink-0">
                      −{Number(b.discount_pct)}%
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    {subtotal > total && (
                      <span className="text-xs text-muted-foreground line-through">
                        {fmt(subtotal)}
                      </span>
                    )}
                    <span className="font-semibold tabular-nums">{fmt(total)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {b.bundle_courses.length} cursos
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
